import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

interface AppModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | false;
  fullWidth?: boolean;
  showCloseButton?: boolean;
  hideBackdropClick?: boolean;
  hideEscapeKey?: boolean;
  actions?: {
    primary?: {
      label: string;
      onClick: () => void;
      variant?: 'contained' | 'outlined' | 'text';
      color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
      startIcon?: React.ReactNode;
      disabled?: boolean;
    };
    secondary?: {
      label: string;
      onClick: () => void;
      variant?: 'contained' | 'outlined' | 'text';
      color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
      startIcon?: React.ReactNode;
      disabled?: boolean;
    };
  };
  icon?: React.ReactNode;
  className?: string;
  contentClassName?: string;
  disablePadding?: boolean;
  fullScreenOnMobile?: boolean;
  variant?: 'default' | 'glass' | 'alert' | 'confirm';
}

const MAX_WIDTH_MAP: Record<string, string> = {
  xs: '444px',
  sm: '600px',
  md: '900px',
  lg: '1200px',
};

function getVariantStyle(variant: string): React.CSSProperties {
  switch (variant) {
    case 'glass':
      return {
        backdropFilter: 'blur(20px)',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
      };
    case 'alert':
    case 'confirm':
    default:
      return {
        background: 'var(--card-bg)',
        border: '1px solid var(--glass-border)',
        boxShadow: 'var(--shadow-md)',
      };
  }
}

type ButtonColor = 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
type ButtonVariant = 'contained' | 'outlined' | 'text';

function getButtonClasses(variant: ButtonVariant, color: ButtonColor): string {
  const base =
    'inline-flex items-center gap-2 rounded-btn px-4 py-2 text-sm font-medium transition-colors ' +
    'disabled:opacity-50 disabled:cursor-not-allowed min-w-[100px] justify-center';

  if (variant === 'contained') {
    const map: Record<ButtonColor, string> = {
      primary:   'bg-primary hover:bg-primary-dark text-white',
      secondary: 'bg-white/10 hover:bg-white/15 text-white border border-white/10',
      error:     'bg-red hover:bg-red/90 text-white',
      success:   'bg-green hover:bg-green/90 text-white',
      warning:   'bg-orange hover:bg-orange/90 text-white',
      info:      'bg-teal hover:bg-teal/90 text-white',
    };
    return `${base} ${map[color] ?? map.primary}`;
  }

  if (variant === 'outlined') {
    const map: Record<ButtonColor, string> = {
      primary:   'border border-primary text-primary hover:bg-primary/10',
      secondary: 'border border-[var(--border-color)] text-[var(--text-color)] hover:bg-[var(--hover-bg)]',
      error:     'border border-red text-red hover:bg-red/10',
      success:   'border border-green text-green hover:bg-green/10',
      warning:   'border border-orange text-orange hover:bg-orange/10',
      info:      'border border-teal text-teal hover:bg-teal/10',
    };
    return `${base} ${map[color] ?? map.secondary}`;
  }

  // text variant
  const map: Record<ButtonColor, string> = {
    primary:   'text-primary hover:bg-primary/10',
    secondary: 'text-[var(--text-secondary)] hover:bg-white/5',
    error:     'text-red hover:bg-red/10',
    success:   'text-green hover:bg-green/10',
    warning:   'text-orange hover:bg-orange/10',
    info:      'text-teal hover:bg-teal/10',
  };
  return `${base} ${map[color] ?? map.secondary}`;
}

const AppModal: React.FC<AppModalProps> = ({
  open,
  onClose,
  title,
  children,
  maxWidth = 'sm',
  fullWidth = true,
  showCloseButton = true,
  hideBackdropClick = false,
  hideEscapeKey = false,
  actions,
  icon,
  className = '',
  contentClassName = '',
  disablePadding = false,
  fullScreenOnMobile = false,
  variant = 'default',
}) => {
  const contentStyle: React.CSSProperties = {
    maxWidth: maxWidth ? MAX_WIDTH_MAP[maxWidth] : undefined,
    width: fullWidth ? '100%' : undefined,
    ...getVariantStyle(variant),
  };

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <Dialog.Portal>
        {/* ── Overlay ── */}
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm animate-fade-in" />

        {/* ── Panel ── */}
        <Dialog.Content
          className={[
            // Default: centred floating dialog
            'fixed z-50 outline-none rounded-[20px] mx-4 animate-slide-up',
            'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
            // Full-screen on mobile override
            fullScreenOnMobile
              ? 'max-sm:inset-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:left-0 max-sm:top-0 max-sm:rounded-none max-sm:m-0 max-sm:h-screen'
              : '',
            className,
          ]
            .filter(Boolean)
            .join(' ')}
          style={contentStyle}
          onInteractOutside={hideBackdropClick ? (e) => e.preventDefault() : undefined}
          onEscapeKeyDown={hideEscapeKey ? (e) => e.preventDefault() : undefined}
        >
          {/* ── Header ── */}
          <div
            className={[
              'flex items-center justify-between p-6 pb-4',
              variant === 'default' ? 'border-b border-[var(--border-color)]' : '',
              variant === 'glass' ? 'bg-white/5' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <div className="flex items-center gap-3">
              {icon && (
                <span className="text-primary text-2xl flex-shrink-0">{icon}</span>
              )}
              <Dialog.Title asChild>
                <h2 className="text-xl font-semibold tracking-tight text-[var(--text-color)]">
                  {title}
                </h2>
              </Dialog.Title>
            </div>

            {showCloseButton && (
              <button
                type="button"
                onClick={onClose}
                aria-label="close"
                className="ml-4 flex-shrink-0 p-1.5 rounded-lg text-[var(--text-secondary)] hover:text-[var(--text-color)] hover:bg-[var(--hover-bg)] transition-colors"
              >
                <X size={20} />
              </button>
            )}
          </div>

          {/* ── Body ── */}
          <Dialog.Description asChild>
            <div
              className={[
                'overflow-y-auto max-h-[60vh] text-[var(--text-color)]',
                '[&::-webkit-scrollbar]:w-1.5',
                '[&::-webkit-scrollbar-track]:bg-white/5 [&::-webkit-scrollbar-track]:rounded',
                '[&::-webkit-scrollbar-thumb]:bg-white/20 [&::-webkit-scrollbar-thumb]:rounded',
                disablePadding ? '' : 'p-6 pt-4',
                contentClassName,
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {children}
            </div>
          </Dialog.Description>

          {/* ── Footer actions ── */}
          {actions && (
            <div className="flex items-center justify-end gap-3 flex-wrap p-6 pt-4 border-t border-[var(--border-color)]">
              {actions.secondary && (
                <button
                  type="button"
                  onClick={actions.secondary.onClick}
                  disabled={actions.secondary.disabled}
                  className={getButtonClasses(
                    actions.secondary.variant ?? 'outlined',
                    actions.secondary.color ?? 'secondary',
                  )}
                >
                  {actions.secondary.startIcon}
                  {actions.secondary.label}
                </button>
              )}
              {actions.primary && (
                <button
                  type="button"
                  onClick={actions.primary.onClick}
                  disabled={actions.primary.disabled}
                  className={getButtonClasses(
                    actions.primary.variant ?? 'contained',
                    actions.primary.color ?? 'primary',
                  )}
                >
                  {actions.primary.startIcon}
                  {actions.primary.label}
                </button>
              )}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default AppModal;
